/* eslint-disable i18next/no-literal-string */
import { CSSProperties } from 'react';
import { FontWeights, IModalStyles, memoizeFunction, mergeStyleSets, useTheme } from '@fluentui/react';
import { getDefaultScrollbarColorSchemeStyle, IAppTheme, ThemeType, useCurrentThemeType } from 'common/theme';
import { getCustomScrollbarStyles } from 'common/utilities/CustomScrollbar';

export interface IWebViewDialogStyles {
  viewport: CSSProperties;
  customScrollBarStyle: CSSProperties;
  header: CSSProperties;
  container: CSSProperties;
  contents: CSSProperties;
}

const getWebViewDialogStyles = memoizeFunction(
  (theme: IAppTheme, themeName: ThemeType, customStyles?: Partial<IWebViewDialogStyles>) =>
    mergeStyleSets(
      {
        viewport: {
          position: 'absolute',
          top: 0,
          height: '100%',
          width: '100%',
          zIndex: 2,
          ...getDefaultScrollbarColorSchemeStyle(themeName),
        },
        customScrollBarStyle: getCustomScrollbarStyles(theme),
        header: {
          maxHeight: '36px',
          color: theme.appColors?.text,
          fontWeight: FontWeights.semibold,
          padding: '0 32px 16px',
        },
        container: {
          height: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          // set base text color so certain (psuedo)elements like list-style can inherit
          color: theme.appColors?.text,
        },
        contents: {
          overflow: 'auto',
        },
      } as IWebViewDialogStyles,
      customStyles
    )
);

const getDialogStyles = memoizeFunction((themeName: ThemeType) =>
  mergeStyleSets({
    container: {
      height: '100%',
      overflow: 'hidden',
    },
    scrollingWrapper: {
      overflow: 'auto',
      position: 'relative',
      ...getDefaultScrollbarColorSchemeStyle(themeName),
    },
    backButtonWrapper: {
      padding: '0 32px 16px',
    },
    closeButtonWrapper: {
      padding: 10, // accounting for padding in icon button
    },
    body: {
      flex: '4 4 auto',
      display: 'flex',
      justifyContent: 'center',
      selectors: {
        p: { margin: '14px 0' },
        'p:first-child': { marginTop: 0 },
        'p:last-child': { marginBottom: 0 },
      },
      '& h1': {
        paddingTop: 0,
      },
    },
    error: {
      textAlign: 'center',
    },
  })
);

export const useDialogStyles = () => {
  const themeName = useCurrentThemeType();
  return getDialogStyles(themeName);
};

export const useWebViewDialogStyles = (customStyles?: Partial<IWebViewDialogStyles>) => {
  const theme = useTheme() as IAppTheme;
  const themeName = useCurrentThemeType();
  return getWebViewDialogStyles(theme, themeName, customStyles);
};

export const iconButtonStyles = memoizeFunction((theme: IAppTheme) => ({
  root: {
    color: theme.appColors?.dialogCloseIcon,
  },
  rootHovered: {
    color: theme.appColors?.dialogCloseIconHover,
  },
  rootPressed: {
    color: theme.appColors?.dialogCloseIconHover,
  },
}));

export const getMergedCustomStylesWithRoot = memoizeFunction((customStyles: Partial<IModalStyles>) =>
  mergeStyleSets(customStyles, {
    root: {
      // no need to use getBackdropFilterStyles here
      backdropFilter: 'blur(8px)',
    },
  })
);
